import { Box, styled } from '@mui/material';
import React, { useMemo } from 'react';
import EmptyDataWarning from '../../../components/EmptyDataWarning';
import { getDasEndpoints } from '../../../apis/dasEndpointApi';
import { useQueries } from '@tanstack/react-query';
import { DasEndpoint } from '../../../types/Device';

const Container = styled(Box)`
  width: 100%;
  min-height: 312px;
  height: 312px;
  padding: 12px 16px;
  border-radius: 4px;
  background: #f3f3f3;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  margin-top: 10px;
`;

const DataItem = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
`;

const DasIdText = styled('div')`
  font-family: 'Noto Sans';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
`;

const FirmwareVersionBox = styled('span')`
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  background-color: #e6a600;
  color: white;
  font-family: 'Noto Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`;

const UnknownBox = styled('span')`
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  background-color: #656565;
  color: white;
  font-family: 'Noto Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`;

const ResultText = styled('div')``;

interface DasIdList {
  value: string;
  isChecked: undefined | boolean;
  endpointId: string;
}

interface DasIdListPanelProps {
  dasIdList: DasIdList[];
}

const DasIdListPanel: React.FC<DasIdListPanelProps> = ({ dasIdList }) => {
  const validationQueries = useQueries({
    queries: dasIdList
      .filter((d) => d.value) // Only query non-empty values
      .map((d) => ({
        queryKey: ['validate-dasid', d.value],
        queryFn: async () => {
          const response = await getDasEndpoints({
            dasId: d.value,
            extendDataPointShadow: true,
          });

          return {
            dasId: d.value,
            device: response.data.data[0],
          };
        },
        enabled: !!d.value,
        staleTime: 30000, // Cache results for 30 seconds
        retry: 1, // Only retry once on failure
      })),
  });

  const deviceMap = useMemo(() => {
    return validationQueries.reduce(
      (acc, query) => {
        if (query.data?.dasId && query.data?.device) {
          acc[query.data.dasId] = query.data.device;
        }
        return acc;
      },
      {} as Record<string, DasEndpoint>,
    );
  }, [validationQueries]);

  return (
    <Container>
      {dasIdList.length > 0 ? (
        dasIdList.map((d) => {
          if (!d.value) return null;

          const device = deviceMap[d.value];
          const query = validationQueries.find(
            (q) => q.data?.dasId === d.value,
          );

          const firmwareVersion =
            device?.shadow?.dataPoint?.firmwareVersion?.value;
          const isLoading = query?.isLoading;
          return (
            <DataItem key={d.value}>
              <DasIdText>{d.value}</DasIdText>
              <ResultText>
                {isLoading ? (
                  'Validating...'
                ) : firmwareVersion ? (
                  <FirmwareVersionBox>{firmwareVersion}</FirmwareVersionBox>
                ) : (
                  <UnknownBox>Unknown</UnknownBox>
                )}
              </ResultText>
            </DataItem>
          );
        })
      ) : (
        <EmptyDataWarning mode="light" />
      )}
    </Container>
  );
};

export default DasIdListPanel;
